import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ContactMailIcon from '@material-ui/icons/ContactMail';
import { Address, AddressSelector, PlaceType } from '../../selectors/AddressSelector';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('Client2AddressPanel');

// Explicit request (EnquiryFormDrawer split): the second client's "Address" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface Client2AddressPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    defaultExpanded: boolean;
    homeCountry: string;
    getAddressLabelForPanel: (buildingName: string, level: string, streetNumber: string, street1: string, street2: string, suburb: string, addressState: string, postCode: string, country: string) => string;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
}

interface Client2AddressPanelBodyProps extends Client2AddressPanelProps {
    formValues: AnyObject;
}

const Client2AddressPanelBody: React.FC<Client2AddressPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, defaultExpanded, homeCountry, getAddressLabelForPanel, createAddressSelectionHandler, createInputChangedHandler,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
        // disabled={disabledAddress(values)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="client2-address-content"
                id="client2-address-header"
                className={classes.AccordionSummary}
            >
                <Typography className={classes.heading}>
                    <ContactMailIcon className={classes.headingIcon} />
                    Main Address
                </Typography>

                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {getAddressLabelForPanel(
                                values.secondClientAddressName,
                                values.secondClientFloor,
                                values.secondClientNumber,
                                values.secondClientStreet1,
                                values.secondClientStreet2,
                                values.secondClientSuburb,
                                values.secondClientState,
                                values.secondClientPostCode,
                                values.secondClientCountry
                            )}
                        </div>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} md={6} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientAddressName"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Building name"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Building name is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={3} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientFloor"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Unit/Level"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Unit/Level is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={3} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientNumber"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Number"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Number is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientStreet1"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Street 1"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Street 1 is required'
                                            : ''
                                    }
                                    value={values.secondClientStreet1}
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'secondClientStreet1')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientStreet2"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Street 2"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Street 2 is required'
                                            : ''
                                    }
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'secondClientStreet2')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientSuburb"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="City/Suburb"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'City/Suburb is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientState"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="State"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'State is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPostCode"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Postal Code"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Postal Code is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientCountry"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Country"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Country is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
Client2AddressPanelBody.displayName = 'Client2AddressPanelBody';

export const Client2AddressPanel: React.FC<Client2AddressPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <Client2AddressPanelBody
                {...panelProps}
                formValues={{
                    secondClientAddressName: values.secondClientAddressName,
                    secondClientFloor: values.secondClientFloor,
                    secondClientNumber: values.secondClientNumber,
                    secondClientStreet1: values.secondClientStreet1,
                    secondClientStreet2: values.secondClientStreet2,
                    secondClientSuburb: values.secondClientSuburb,
                    secondClientState: values.secondClientState,
                    secondClientPostCode: values.secondClientPostCode,
                    secondClientCountry: values.secondClientCountry,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
Client2AddressPanel.displayName = 'Client2AddressPanel';
